import React from "react";
import styled, { css } from "styled-components";
import Rule from "../../../Rule";
import Image from "../../../Image";
import Box from "../../../Box";
import Gridbox from "../../../Gridbox";
import Heading from "../../../Heading";
import CaseStudiesCard from "../CaseStudiesCard";
import DesignSystemsContribution from "../../../../img/casestudy/design-systems/design-system-contribution.png";
import Stack from "../../../Stack";
import Airwallex from "../../../../img/airwallex-contribution.png";
import BlogLayout from "../BlogLayout";

const PrinciplesHarvestModel = styled.div`
  max-width: initial;
  @media (min-width: 1400px) {
    max-width: 340px;
  }
`;

const ContentBoxColumn = styled.div`
  display: grid;
  gap: 48px;
  margin-bottom: var(--spacing-clear);
  @media (min-width: 1400px) {
    gap: 96px;
    grid-template-columns: auto auto;
  }
`;

const ReflectionsBox = styled.div`
  display: grid;
  margin: 0;
  gap: 0;
  grid-template-columns: 1fr;
  @media (min-width: 1400px) {
    gap: 96px;
    grid-template-columns: 1fr 1fr;
  }
  p {
    margin: 0;
  }
`;

const ContentBox = styled.div`
  margin-bottom: var(--spacing-clear);
  position: relative;
  &:last-child {
    margin-bottom: 0;
  }
  p,
  ol,
  ul {
    max-width: 64ch;
    &:last-child,
    li:last-child {
      margin-bottom: var(--spacing-clear);
    }
  }
  p,
  ol,
  ul {
    max-width: 64ch;
  }
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(var(--grid-column), 1fr);
  background: #1c1c1c;
  margin-bottom: 0;
`;

const CreatingNewGrid = styled(CaseStudyGrid)`
  --grid-column: 5;
`;

const EnhanceExistingGrid = styled(CaseStudyGrid)`
  --grid-column: 4;
`;

const CaseStudyGridStep = styled.h6`
  width: auto;
  display: inline-block;
  color: #f56fd6;
  margin-bottom: 2px;
  margin-top: 0;

  ${(props) =>
    props.phase === "domain" &&
    css`
      color: #89e2fd;
    `}

  ${(props) =>
    props.phase === "2" &&
    css`
      color: #89e2fd;
    `}

    ${(props) =>
      props.phase === "platform" &&
      css`
        color: #ff9fe8;
      `}

    ${(props) =>
      props.phase === "4" &&
      css`
        color: #fedd68;
      `}

    ${(props) =>
      props.phase === "5" &&
      css`
        color: #fedd68;
      `}
`;

const CaseStudyGridItem = styled.div`
  font-size: 1.3rem;
  padding: var(--spacing-medium);
  margin-bottom: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji",
    "Segoe UI Emoji", "Segoe UI Symbol";
  &:nth-child(even) {
    background: #272727;
  }
`;

const CaseStudyGridHeading = styled.h4`
  background: #1c1c1c;
  padding: 1.6rem 2.4rem;
  text-align: center;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  grid-column: 1 / calc(var(--grid-column) + 1);
  margin: 0;
`;

const OverflowScrollShadow = styled.div`
  position: relative;
  &:before {
    position: absolute;
    width: 1px;
    height: 100%;
    background: rgb(0 0 0 / 48%);
    content: "";
    right: 0;
    box-shadow: -1px 0 1px 0 rgb(0 0 0 / 0.4), -2px 0 2px 0 rgb(0 0 0 / 0.3), -3px 0 3px 0 rgb(0 0 0 / 0.2);
  }
`;

const OverflowScroll = styled.div`
  overflow: scroll;
`;

const DesigningContributionModel = (
  <CaseStudiesCard id="contribution">
    <Heading
      variant="smaller"
      style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
    >
      Case Study
    </Heading>
    <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
      Enabling team contributions while keeping the system consistent.
    </p>
    <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

    <Stack spacing="var(--spacing-xx-large)">
      <Box>
        <BlogLayout wide>
          <Box style={{ marginBottom: "var(--spacing-large)" }}>
            <Heading variant="larger" style={{ margin: 0 }}>
              MYOB
            </Heading>
            <p
              style={{
                fontWeight: "regular",
                fontSize: "var(--font-size-heading-small)",
                margin: "0",
              }}
            >
              The Harvest Model
            </p>
          </Box>

          <ContentBoxColumn style={{ marginBottom: "var(--spacing-large)" }}>
            <div>
              <p>
                Every organisation is unique, and contribution models must reflect how teams operate. At MYOB, this was
                no exception. We recognised early on that while some teams were enthusiastic about contributing directly
                to the design system (Feelix), others lacked confidence or the capacity to create production-ready
                components.
              </p>

              <p>
                At the same time, MYOB was undergoing a full platform re-architecture. This created both a challenge and
                an opportunity of how could we enable teams to innovate quickly, without creating silos or introducing
                one-off patterns.
              </p>

              <p>
                In collaboration with lead architects and the design function, we developed a flexible "Harvest" model
                that respected each team’s autonomy while maintaining alignment across the organisation.
              </p>
            </div>
            <PrinciplesHarvestModel>
              <ContentBox>
                <Heading variant="small">Principles:</Heading>
                <ul>
                  <li>
                    <strong>Empower teams to move fast</strong> with design and implementation.
                  </li>
                  <li>
                    <strong>Ensure visibility</strong> of new patterns within the design function.
                  </li>
                  <li>
                    <strong>Promote maturity and standardisation</strong> before formal adoption.
                  </li>
                  <li>
                    <strong>Minimise bottlenecks</strong> and reduce reliance on the core design system team.
                  </li>
                </ul>
              </ContentBox>
            </PrinciplesHarvestModel>
          </ContentBoxColumn>
        </BlogLayout>

        <Stack spacing="var(--spacing-medium)">
          <Image dark src={DesignSystemsContribution} alt="Harvest Model Sketch" caption="Harvest Model Sketch" />
          <Box mt="large" mb="medium">
            <CaseStudyGridHeading id="contributingtofeelix">Creating New Components</CaseStudyGridHeading>
            <OverflowScrollShadow style={{ marginBottom: 0 }}>
              <OverflowScroll style={{ marginBottom: 0 }}>
                <Box minWidth="1058px" style={{ marginBottom: 0 }}>
                  <Gridbox gridTemplateColumns="2fr 3fr" mb="clear">
                    <Box
                      backgroundColor="#89e2fd"
                      mb="clear"
                      padding="small"
                      textAlign="center"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Heading variant="medium" mb="clear" color="black">
                        Release customer value
                      </Heading>
                      <Box as="p" color="black !important" maxWidth="34rem" mb="smallest">
                        Test with the market
                      </Box>
                    </Box>

                    <Box
                      backgroundColor="#ff9fe8"
                      mb="clear"
                      padding="small"
                      textAlign="center"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Heading variant="medium" mb="clear" color="black">
                        Maintain quality of the system
                      </Heading>
                      <Box as="p" color="black !important" maxWidth="42rem" mb="smallest">
                        Harvest the successful components
                      </Box>
                    </Box>
                  </Gridbox>

                  <CreatingNewGrid>
                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="domain">Phase 1</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="12rem" mb="small">
                        Design, test & align
                      </Heading>
                      <Rule inverted />
                      <ol>
                        <li>Prove that Feelix can’t solve your problem</li>
                        <li>Critique the alternative solution(s) to Feelix</li>
                        <li>Circulate the alternative solution(s) to Feelix</li>
                        <li>Document the new solution in Labs</li>
                      </ol>
                    </CaseStudyGridItem>

                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="domain">Phase 2</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="14rem" mb="small">
                        Build & use the component
                      </Heading>
                      <Rule inverted />
                      <ol>
                        <li>
                          The designer has completed the required supporting component documentation and there is clear
                          alignment within the team Leadership
                        </li>
                        <li>
                          The development team can begin building the new component and then use the new experience to
                          release the to market.
                        </li>
                      </ol>
                    </CaseStudyGridItem>

                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="platform">Phase 3</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="9rem" mb="small">
                        Submit to the DSC
                      </Heading>
                      <Rule inverted />
                      <ol>
                        <li>Designers can propose a validated component for the Feelix Design System.</li>
                        <li>
                          Submit the working component and documentation outlining the problem it solves to the DSC.
                        </li>
                        <li>The DSC will review and decide if the proposal is appropriate for Feelix.</li>
                      </ol>
                    </CaseStudyGridItem>

                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="platform">Phase 4</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="9rem" mb="small">
                        Feelix adopts it
                      </Heading>
                      <Rule inverted />
                      <ol>
                        <li>The data is showcasing broad adoption of the component</li>
                        <li>A significant customer problem is solved</li>
                        <li>The component will be added to Feelix.</li>
                      </ol>
                    </CaseStudyGridItem>
                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="platform">Phase 5</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="9rem" mb="small">
                        Team replaces it
                      </Heading>
                      <Rule inverted />
                      <ol>
                        <li>Feelix has added the component to the Design System.</li>
                        <li>
                          The Design System team has requested the development team to upgrade to the Feelix version
                          with the newly added component.
                        </li>
                      </ol>
                    </CaseStudyGridItem>
                  </CreatingNewGrid>
                </Box>
              </OverflowScroll>
            </OverflowScrollShadow>
          </Box>

          <BlogLayout wide>
            <ContentBoxColumn>
              <ContentBox>
                <Heading variant="small">Challenges</Heading>

                <p>
                  <strong>Misalignment of expectations:</strong> Some teams saw the Feelix team as a delivery function
                  for their roadmap work, rather than partners in maintaining a shared system. This limited shared
                  ownership and led to unrealistic assumptions about our role.
                </p>
                <p>
                  <strong>Cultural resistance:</strong> In parts of the design function, there was hesitation to follow
                  the contribution process. Some teams tried to offload work to the system team instead of
                  collaborating, slowing down adoption and shared learning.
                </p>
              </ContentBox>

              <ContentBox>
                <Heading variant="small">How We Addressed This</Heading>

                <p>
                  <strong>Roles & Responsibilities:</strong> We introduced clear documentation outlining what the Design
                  System team owns, supports, and doesn’t take on. This helped set expectations and encouraged
                  accountability, though it also revealed a need for deeper cultural alignment.
                </p>
                <p>
                  <strong>Education & Coaching:</strong> We ran hands-on DSC sessions focused on contribution practices,
                  supported by designers and engineers from Feelix. By pairing with teams to shape components and build
                  together, we reduced friction and built capability.
                </p>
              </ContentBox>
            </ContentBoxColumn>
          </BlogLayout>

          <Box mt="large" mb="medium">
            <CaseStudyGridHeading>Enhancing Existing Components</CaseStudyGridHeading>
            <OverflowScrollShadow style={{ marginBottom: 0 }}>
              <OverflowScroll style={{ marginBottom: 0 }}>
                <Box minWidth="1058px" style={{ marginBottom: 0 }}>
                  <Gridbox gridTemplateColumns="1fr 3fr" mb="clear">
                    <Box
                      backgroundColor="#89e2fd"
                      mb="clear"
                      padding="small"
                      textAlign="center"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Heading variant="medium" mb="clear" color="black">
                        Create Prototype
                      </Heading>
                      <Box as="p" color="black !important" maxWidth="22rem" mb="smallest">
                        Test with the market
                      </Box>
                    </Box>

                    <Box
                      backgroundColor="#ff9fe8"
                      mb="clear"
                      padding="small"
                      textAlign="center"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Heading variant="medium" mb="clear" color="black">
                        Maintain quality of the system
                      </Heading>
                      <Box as="p" color="black !important" maxWidth="42rem" mb="smallest">
                        Harvest the successful enhancement
                      </Box>
                    </Box>
                  </Gridbox>

                  <EnhanceExistingGrid>
                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="domain">Phase 1</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="12rem" mb="small">
                        Design, test & align
                      </Heading>

                      <Rule inverted />

                      <ol>
                        <li>Prove that Feelix can’t solve your problem</li>
                        <li>Critique the alternative solution(s) to Feelix</li>
                        <li>Circulate the alternative solution(s) to Feelix</li>
                        <li>Document the new solution in Labs</li>
                        {/* <li>
                      Follow the <Links href="#phaseOneSteps">"Design, validate and align" steps</Links>
                    </li> */}
                      </ol>
                    </CaseStudyGridItem>

                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="platform">Phase 2</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="12rem" mb="small">
                        Submit to the DSC
                      </Heading>
                      <Rule inverted />
                      <ol>
                        <li>
                          Designers enhancing a Feelix component must add usage guidelines and the Sketch file to the
                          shared library.
                        </li>
                        <li>
                          They should share the design direction and component template, explaining the problem it
                          solves.
                        </li>
                        <li>
                          The DSC will review and discuss the enhancement. If there are no concerns and all
                          documentation is complete, the enhancement is approved.
                        </li>
                      </ol>
                    </CaseStudyGridItem>
                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="platform">Phase 3</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="9rem" mb="small">
                        Feelix adopts it
                      </Heading>
                      <Rule inverted />

                      <ol>
                        <li>
                          The successful enhancement will be added to the Feelix Coordination Board and can be
                          prioritised accordingly.
                        </li>
                        <li>
                          Once Feelix has completed the enhanced Feelix component, teams will be notified what version
                          of Feelix it has been added to, so they can begin the next phase.
                        </li>
                      </ol>
                    </CaseStudyGridItem>
                    <CaseStudyGridItem>
                      <CaseStudyGridStep phase="platform">Phase 4</CaseStudyGridStep>
                      <Heading variant="smaller" m="clear" width="14rem" mb="small">
                        Update & test Feelix version
                      </Heading>
                      <Rule inverted />
                      <ol>
                        <li>The team has been notified what version of Feelix the enhancement has been added to.</li>
                        <li>
                          The team(s) can now plan for an appropriate time to upgrade their version of Feelix to access
                          the new feature or behaviour.
                        </li>
                      </ol>
                    </CaseStudyGridItem>
                  </EnhanceExistingGrid>
                </Box>
              </OverflowScroll>
            </OverflowScrollShadow>
          </Box>
        </Stack>

        <BlogLayout wide>
          <ContentBox>
            <Heading variant="small">Reflections</Heading>
            <ReflectionsBox>
              <p>
                The harvest-based contribution model at MYOB helped shift the mindset from designing for the system to
                designing for customers first, then bringing proven solutions back into the system. It enabled teams to
                move quickly, validate ideas in market, and share learnings that shaped the evolution of Feelix.
              </p>
              <p>
                While success relied on strong communication, cultural buy-in, and a shared sense of ownership, the
                result was a more adaptive, scalable, and relevant system. One that could keep pace with the diversity
                of product needs without sacrificing cohesion or quality.
              </p>
            </ReflectionsBox>
          </ContentBox>
        </BlogLayout>
      </Box>

      <BlogLayout wide>
        <Box style={{ marginBottom: "var(--spacing-large)" }}>
          <Heading variant="larger" style={{ margin: 0 }}>
            Airwallex
          </Heading>
          <p
            style={{
              fontWeight: "regular",
              fontSize: "var(--font-size-heading-small)",
              margin: "0",
            }}
          >
            Contribution-Driven Model
          </p>
        </Box>

        <ContentBoxColumn style={{ marginBottom: "var(--spacing-large)" }}>
          <div>
            <p>
              At Airwallex, the culture of contribution was already strong, with engineers and developers actively
              participating in the system. My role focused on guiding designers through critique and prototyping,
              supporting engineers as they built components, and reviewing pull requests to ensure quality and
              alignment.
            </p>

            <p>
              Rather than creating a strict contribution guide, I onboard new designers by taking them through a mapped
              journey of contribution: from design critiques and product shareouts with stakeholders, through sign-off,
              to build and integration. The goal is simple: align across teams, improve the user experience, and confirm
              business and strategy alignment before a component enters the system.
            </p>

            <p>
              This approach leverages existing rituals to provide clear indicators of when a component is ready for the
              system. It reinforces a culture of collaboration and shared ownership, helping the system evolve
              efficiently while maintaining cohesion and quality.
            </p>
          </div>
          <PrinciplesHarvestModel>
            <ContentBox>
              <Heading variant="smaller" mb="smaller" mt="clear" color="#ff73df">
                Principles:
              </Heading>
              <ul>
                <li>
                  <strong>Empower teams:</strong> Prototype, test, and propose components without waiting for approval.
                </li>
                <li>
                  <strong>Align early:</strong> Gather feedback through critiques and shareouts.
                </li>
                <li>
                  <strong>Validate before adoption:</strong> Test components in workflows for usability and feasibility.
                </li>
                <li>
                  <strong>Shared ownership:</strong> Everyone involved is accountable for quality and cohesion.
                </li>
              </ul>
            </ContentBox>
          </PrinciplesHarvestModel>
        </ContentBoxColumn>
        <Image dark src={Airwallex} alt="Airwallex Contribution Model" caption="Airwallex Contribution Model" />
      </BlogLayout>
    </Stack>
  </CaseStudiesCard>
);

export default DesigningContributionModel;
